import { Button, Stack, Text } from '@adc/parallax-component-library';
import { Dialog } from '@tamagui/dialog';

import { useModal } from '~/features/modals/useModal';
import { buildTestId } from '~/utils/buildTestId';

import { ModalOverlay } from '../ModalOverlay';

type ExitIntentModalProps = {
  title?: string;
  content: string;
  onExitConfirmation?: () => void;
};

const TEST_ID = 'exit-intent' as const;

export const ExitIntentModal = ({
  title,
  content,
  onExitConfirmation,
}: ExitIntentModalProps) => {
  const { closeModal } = useModal();
  const onClose = () => {
    closeModal('ExitIntent');
  };

  const onOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  return (
    <Dialog open onOpenChange={onOpenChange} modal>
      <Dialog.Portal>
        <ModalOverlay />

        <Dialog.Content
          key="content"
          elevate
          animateOnly={['transform', 'opacity']}
          animation={[
            'quick',
            {
              opacity: {
                overshootClamping: true,
              },
            },
          ]}
          enterStyle={{ x: 0, y: 20, opacity: 0 }}
          exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
          padding="$6"
          paddingBottom="$6"
          borderWidth="$0"
          maxWidth={800}
          width="100%"
          borderRadius="$1.5"
          backgroundColor="$surface.background"
          alignItems="flex-start"
        >
          <Stack
            flexDirection="column"
            gap="$4"
            paddingTop="$2"
            paddingRight="$2"
            paddingBottom="$2"
          >
            <Text
              type="$h3.strong"
              testID={buildTestId({ testID: TEST_ID, suffix: 'title' })}
            >
              {title ?? 'Are you sure you want to leave?'}
            </Text>

            <Text
              type="$bodySmall.default"
              color="$text.60"
              paddingTop="$3"
              testID={buildTestId({ testID: TEST_ID, suffix: 'content' })}
            >
              {content ? content : ''}
            </Text>
          </Stack>

          <Stack
            flexDirection="row"
            justifyContent="flex-end"
            gap="$4"
            paddingTop="$5"
            paddingRight="$2"
            paddingBottom="$2"
            width="100%"
          >
            <Button
              variant="secondary"
              label="Back"
              onPress={onClose}
              testID={buildTestId({ testID: TEST_ID, suffix: 'back' })}
              // @ts-expect-error Parallax doesn't type this prop
              minWidth={168}
            />
            <Button
              variant="primary"
              label="Leave"
              onPress={() => {
                onExitConfirmation?.();
                onClose();
              }}
              testID={buildTestId({ testID: TEST_ID, suffix: 'leave' })}
              // @ts-expect-error Parallax doesn't type this prop
              minWidth={168}
            />
          </Stack>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
};
